import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

export interface AlertReasonDialogData {
  alertId: number;
  action: 'RESOLVED' | 'SUPPRESSED';
  alertInfo?: {
    skuCode?: string;
    skuName?: string;
  };
}

export interface AlertReasonDialogResult {
  confirmed: boolean;
  reason?: string;
}

@Component({
  selector: 'app-alert-reason-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    TextareaModule,
    AppButtonComponent,
  ],
  templateUrl: './alert-reason-dialog.component.html',
  styleUrl: './alert-reason-dialog.component.css',
})
export class AlertReasonDialogComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);

  @Input() visible = false;
  @Input() data: AlertReasonDialogData | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() result = new EventEmitter<AlertReasonDialogResult>();

  reasonForm!: FormGroup;

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.reasonForm = this.fb.group({
      reason: ['', [Validators.required, Validators.maxLength(500)]],
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.visible && this.data) {
      this.reasonForm.reset();
      this.reasonForm.patchValue({ reason: '' });
    }
  }

  onConfirm(): void {
    if (this.reasonForm.invalid) {
      this.reasonForm.markAllAsTouched();
      return;
    }
    const reason = this.reasonForm.get('reason')?.value?.trim() || '';
    this.result.emit({ confirmed: true, reason });
    this.closeDialog();
  }

  onCancel(): void {
    this.result.emit({ confirmed: false });
    this.closeDialog();
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.reasonForm.reset();
  }

  getReasonError(): string {
    const reasonControl = this.reasonForm.get('reason');
    if (reasonControl?.errors && (reasonControl.touched || reasonControl.dirty)) {
      if (reasonControl.errors['required']) {
        return this.translateService.instant('stock_alert.dialog.reason_required');
      }
      if (reasonControl.errors['maxlength']) {
        return this.translateService.instant('common.validation.maxlength', {
          fieldName: this.translateService.instant('stock_alert.dialog.reason_label'),
          requiredLength: reasonControl.errors['maxlength'].requiredLength,
        });
      }
    }
    return '';
  }

  getDialogTitle(): string {
    if (!this.data) return '';
    const actionKey = this.data.action === 'RESOLVED'
        ? 'stock_alert.actions.resolve'
        : 'stock_alert.actions.suppress';
    return this.translateService.instant('stock_alert.dialog.title', { action: this.translateService.instant(actionKey) });
  }

  getDialogMessage(): string {
    if (!this.data) return '';
    const actionKey = this.data.action === 'RESOLVED'
        ? 'stock_alert.dialog.resolve_message'
        : 'stock_alert.dialog.suppress_message';
    return this.translateService.instant(actionKey, { skuCode: this.data.alertInfo?.skuCode || '', skuName: this.data.alertInfo?.skuName || '' });
  }
}

